import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Box,
  Typography,
  Autocomplete,
  TextField
} from "@mui/material";
import Cookies from "universal-cookie";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import CustomDateRange from "../../Components/CustomDateRange";
import transactionAPI from "../../Network/Transaction";
import transactionStatusAPI from "../../Network/TransactionStatus";
import unitAPI from "../../Network/Unit";
import mizanListAPI from "../../Network/MizanList";
import geofenceAPI from "../../Network/Geofence";
import userAPI from "../../Network/User";
import municipalityAPI from "../../Network/Municipality";
import Title from "./Title";
import Grid from "../Grid/Grid";

const cookies = new Cookies();

export default function ReportUnitTransactionSummary() {
  const [t] = useTranslation("common");
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    unit_id: null,
    unit: null,
    search_account_id: null,
    account: null,
    municipality_id: null,
    municipality: null,
    mizan_id: null,
    mizan_selected: null,
    source_geofence_id: null,
    sourceGeofence: null,
    destination_geofence_id: null,
    destinationGeofence: null,
    creationDateFrom: moment().startOf('month').toDate(),
    creationDateTo: moment().endOf('day').toDate(),
    creation_date_from: moment().startOf('month').format('YYYY-MM-DD HH:mm:ss'),
    creation_date_to: moment().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
    all_time: 0
  });

  const [reload, setReload] = useState(false);

  const [units, setUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [municipalities, setMunicipalities] = useState([]);
  const [loadingMunicipalities, setLoadingMunicipalities] = useState(false);
  const [mizans, setMizans] = useState([]);
  const [loadingMizans, setLoadingMizans] = useState(false);
  const [sourceGeofences, setSourceGeofences] = useState([]);
  const [loadingSourceGeofences, setLoadingSourceGeofences] = useState(false);
  const [destinationGeofences, setDestinationGeofences] = useState([]);
  const [loadingDestinationGeofences, setLoadingDestinationGeofences] = useState(false);

  const timerAccounts = useRef(null);
  const timerSourceGeofences = useRef(null);
  const timerDestinationGeofences = useRef(null);
  const [pageSize, setPageSize] = useState(100);
  const [pageNumber, setPageNumber] = useState(1);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const timerUnits = useRef(null);

  const headerObject = {
    username: cookies.get("user_name") ? cookies.get("user_name") : null,
    user_id: cookies.get("user_id") ? cookies.get("user_id") : null,
    account_name: cookies.get("account_name") ? cookies.get("account_name") : null,
    account_id: cookies.get("account_id") ? cookies.get("account_id") : null,
    authHash: cookies.get("authHash") ? cookies.get("authHash") : null,
    sid: cookies.get("sid") ? cookies.get("sid") : null,
    baseurl: cookies.get("baseUrl") ? cookies.get("baseUrl") : null,
    id: cookies.get("id") ? cookies.get("id") : null,
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await transactionAPI.getUnitTransactionsSummary({
        params: headerObject,
        data: {
          unit_id: filters.unit_id,
          search_account_id: filters.search_account_id,
          municipality_id: filters.municipality_id,
          mizan_id: filters.mizan_id,
          source_geofence_id: filters.source_geofence_id,
          destination_geofence_id: filters.destination_geofence_id,
          creation_date_from: filters.creation_date_from,
          creation_date_to: filters.creation_date_to,
        }
      });

      if (response.status && response?.data?.status) {
        const data = response?.data?.data || [];
        setRows(data.map((row, index) => ({ ...row, id: index })));
        setRowsTotal(data.length);
        setPageCount(Math.ceil(data.length / pageSize));
      } else {
        toast.error(response?.data?.message || t("NETWORK_MESSAGES.unknownError"));
      }
    } catch (error) {
      toast.error(error.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [reload]);

  const handleSearchTermUnitChange = (newValue) => {
    if (timerUnits.current) clearTimeout(timerUnits.current);
    timerUnits.current = setTimeout(async () => {
      if (newValue?.length >= 3) {
        setLoadingUnits(true);
        const result = await unitAPI.getUnitList({
          params: { ...headerObject, name: newValue, page_size: 20, page: 1 }
        });
        setLoadingUnits(false);
        if (result.status && result?.data?.data?.data) {
          setUnits(result?.data?.data?.data);
        }
      } else {
        setUnits([]);
      }
    }, 1000);
  };

  const handleSearchTermAccountChange = (newValue) => {
    if (timerAccounts.current) clearTimeout(timerAccounts.current);
    timerAccounts.current = setTimeout(async () => {
      if (newValue?.length >= 3 || newValue === "") {
        setLoadingAccounts(true);
        const result = await userAPI.getAccounts({
          params: { ...headerObject, name: newValue, page_size: 100, page: 1 }
        });
        setLoadingAccounts(false);
        if (result.status && result?.data?.data?.data) {
          setAccounts(result?.data?.data?.data);
        }
      } else {
        setAccounts([]);
      }
    }, newValue === "" ? 0 : 1000);
  };

  const handleSearchTermSourceGeofenceChange = (newValue) => {
    if (timerSourceGeofences.current) clearTimeout(timerSourceGeofences.current);
    timerSourceGeofences.current = setTimeout(async () => {
      if (newValue?.length >= 3 || newValue === "") {
        setLoadingSourceGeofences(true);
        const result = await geofenceAPI.getGeofenceList({
          params: { ...headerObject, name: newValue, port_type_ids: [1, 3], page_size: 100, page: 1 }
        });
        setLoadingSourceGeofences(false);
        if (result.status && result?.data?.data?.data) {
          setSourceGeofences(result?.data?.data?.data);
        }
      } else {
        setSourceGeofences([]);
      }
    }, newValue === "" ? 0 : 1000);
  };

  const handleSearchTermDestinationGeofenceChange = (newValue) => {
    if (timerDestinationGeofences.current) clearTimeout(timerDestinationGeofences.current);
    timerDestinationGeofences.current = setTimeout(async () => {
      if (newValue?.length >= 3 || newValue === "") {
        setLoadingDestinationGeofences(true);
        const result = await geofenceAPI.getGeofenceList({
          params: { ...headerObject, name: newValue, port_type_ids: [2, 3], page_size: 100, page: 1 }
        });
        setLoadingDestinationGeofences(false);
        if (result.status && result?.data?.data?.data) {
          setDestinationGeofences(result?.data?.data?.data);
        }
      } else {
        setDestinationGeofences([]);
      }
    }, newValue === "" ? 0 : 1000);
  };

  const loadInitialLookups = async () => {
    setLoadingMunicipalities(true);
    setLoadingMizans(true);
    try {
      const mResult = await municipalityAPI.getMunicipalityList({ params: headerObject });
      if (mResult.status && mResult?.data?.data) setMunicipalities(mResult?.data?.data);
      
      const mizResult = await mizanListAPI.getMizanLookup({ params: headerObject });
      if (mizResult.status && mizResult?.data?.data) setMizans(mizResult?.data?.data);

      handleSearchTermAccountChange("");
      handleSearchTermSourceGeofenceChange("");
      handleSearchTermDestinationGeofenceChange("");
    } catch (e) { console.error(e); }
    setLoadingMunicipalities(false);
    setLoadingMizans(false);
  };

  useEffect(() => {
    loadInitialLookups();
  }, []);

  const clearFilters = () => {
    setFilters({
      unit_id: null,
      unit: null,
      search_account_id: null,
      account: null,
      municipality_id: null,
      municipality: null,
      mizan_id: null,
      mizan_selected: null,
      source_geofence_id: null,
      sourceGeofence: null,
      destination_geofence_id: null,
      destinationGeofence: null,
      creationDateFrom: moment().startOf('month').toDate(),
      creationDateTo: moment().endOf('day').toDate(),
      creation_date_from: moment().startOf('month').format('YYYY-MM-DD HH:mm:ss'),
      creation_date_to: moment().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      all_time: 0
    });
  };

  const handleRedirect = (unit_id, status_ids, status_selected, unit_name) => {
    const dashboardFilters = {
      unit_id: unit_id || filters.unit_id,
      unit: unit_name ? { id: unit_id, name: unit_name } : filters.unit,
      unit_account_id: filters.search_account_id,
      account: filters.account,
      municipality_id: filters.municipality_id,
      municipality: filters.municipality,
      mizan_id: filters.mizan_id,
      mizan_selected: filters.mizan_selected,
      source_geofence_id: filters.source_geofence_id,
      sourceGeofence: filters.sourceGeofence,
      destination_geofence_id: filters.destination_geofence_id,
      destinationGeofence: filters.destinationGeofence,
      creationDateFrom: filters.creationDateFrom,
      creationDateTo: filters.creationDateTo,
      creation_date_from: filters.creation_date_from,
      creation_date_to: filters.creation_date_to,
      all_time: filters.all_time,
      transaction_status_ids: status_ids,
      transaction_status_selected: status_selected
    };
    localStorage.setItem('dashboardFilters', JSON.stringify(dashboardFilters));
    navigate('/system/transactions_custom');
  };

  const [gridColumns, setGridColumns] = useState([
    { field: 'unit_name', headerName: "اسم المركبة", flex: 1.5, headerAlign: 'center', align: 'center' },
    { field: 'total_transactions', headerName: "عدد الشحنات الكلي", flex: 1, headerAlign: 'center', align: 'center' },
    {
      field: 'total_not_audited_transactions',
      headerName: "عدد الشحنات غير المدققة",
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box
          sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }}
          onClick={() => handleRedirect(params.row.unit_id, [1, 2], [
            { id: 1, name: "غير مدقق" },
            { id: 2, name: "قيد التدقيق" }
          ], params.row.unit_name)}
        >
          {params.value}
        </Box>
      )
    },
    {
      field: 'total_audited_and_valid_transactions',
      headerName: "عدد الشحنات المعتمدة",
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box
          sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }}
          onClick={() => handleRedirect(params.row.unit_id, [4], [
            { id: 4, name: "معتمد" }
          ], params.row.unit_name)}
        >
          {params.value}
        </Box>
      )
    },
    {
      field: 'total_audited_and_not_valid_transactions',
      headerName: "عدد الشحنات غير المعتمدة",
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box
          sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }}
          onClick={() => handleRedirect(params.row.unit_id, [3], [
            { id: 3, name: "غير معتمد" }
          ], params.row.unit_name)}
        >
          {params.value}
        </Box>
      )
    },
    { field: 'total_weights_of_transactions', headerName: "الوزن الكلي للشحنات", flex: 1, headerAlign: 'center', align: 'center', renderCell: (params) => params.value ? params.value.toLocaleString() : '0' },
    { field: 'total_weight_for_not_audited_transactions', headerName: "الوزن غير المدقق", flex: 1, headerAlign: 'center', align: 'center', renderCell: (params) => params.value ? params.value.toLocaleString() : '0' },
    { field: 'total_weight_for_audited_and_valid_transactions', headerName: "الوزن المعتمد", flex: 1, headerAlign: 'center', align: 'center', renderCell: (params) => params.value ? params.value.toLocaleString() : '0' },
    { field: 'total_weight_for_audited_and_not_valid_transactions', headerName: "الوزن غير المعتمد", flex: 1, headerAlign: 'center', align: 'center', renderCell: (params) => params.value ? params.value.toLocaleString() : '0' }
  ]);

  const handlePageChange = (newPage) => {
    setPageNumber(newPage + 1);
  };

  const searchFunction = () => {
    setReload(!reload);
    setPageNumber(1);
  };

  return (
    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Title title="ملخص حركات المركبات" />
      
      <Grid
        rows={rows}
        columns={gridColumns}
        setColumns={setGridColumns}
        hasColumnsHidShow={true}
        showAllColumns={true}
        useCookies={true}
        preferenceKey="report_unit_transaction_summary"
        pageSize={pageSize}
        setPageSize={setPageSize}
        pageNumber={pageNumber}
        setPageNumber={setPageNumber}
        loading={loading}
        rowsTotal={rowsTotal}
        pageCount={pageCount}
        handlePageChange={handlePageChange}
        paginationMode="client"
        fixHeight={'calc(100vh - 300px)'}
        searchFunction={searchFunction}
        clearFunction={clearFilters}
        filterChilds={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 1 }}>
             <Autocomplete
              sx={{ width: '100%' }}
              options={units}
              getOptionLabel={(option) => option.name || ""}
              loading={loadingUnits}
              onInputChange={(event, newInputValue) => handleSearchTermUnitChange(newInputValue)}
              onChange={(event, newValue) => setFilters({ ...filters, unit_id: newValue?.id || newValue?.unit_id || newValue?.WialonId, unit: newValue })}
              value={filters.unit}
              renderInput={(params) => <TextField {...params} label={t("UNITS.name")} size="small" variant="standard" />}
            />
            <Autocomplete
              sx={{ width: '100%' }}
              options={accounts}
              getOptionLabel={(option) => option.name || ""}
              loading={loadingAccounts}
              onInputChange={(event, newInputValue) => handleSearchTermAccountChange(newInputValue)}
              onChange={(event, newValue) => setFilters({ ...filters, search_account_id: newValue?.id || newValue?.account_id, account: newValue })}
              value={filters.account}
              renderInput={(params) => <TextField {...params} label="العائدية او الحساب" size="small" variant="standard" />}
            />
            <Autocomplete
              sx={{ width: '100%' }}
              options={municipalities}
              loading={loadingMunicipalities}
              onChange={(event, newValue) => setFilters({ ...filters, municipality_id: newValue?.id || newValue?.MunicipalityId, municipality: newValue })}
              value={filters.municipality}
              getOptionLabel={(option) => option?.name || option?.municipality_name || ""}
              renderInput={(params) => <TextField {...params} label={t("GLOBAL.municipalities")} size="small" variant="standard" />}
            />
            <Autocomplete
              sx={{ width: '100%' }}
              options={mizans}
              getOptionLabel={(option) => option.name || ""}
              loading={loadingMizans}
              onChange={(event, newValue) => setFilters({ ...filters, mizan_id: newValue?.id || newValue?.MizanId || null, mizan_selected: newValue })}
              value={filters.mizan_selected}
              renderInput={(params) => <TextField {...params} label={t("MIZAN_UNITS.mizan") || "الميزان"} size="small" variant="standard" />}
            />
            <Autocomplete
              sx={{ width: '100%' }}
              options={sourceGeofences}
              getOptionLabel={(option) => option.name || ""}
              loading={loadingSourceGeofences}
              onInputChange={(event, newInputValue) => handleSearchTermSourceGeofenceChange(newInputValue)}
              onChange={(event, newValue) => setFilters({ ...filters, source_geofence_id: newValue?.id, sourceGeofence: newValue })}
              value={filters.sourceGeofence}
              renderInput={(params) => <TextField {...params} label={t("TRANSACTIONS.source_geofence")} size="small" variant="standard" />}
            />
            <Autocomplete
              sx={{ width: '100%' }}
              options={destinationGeofences}
              getOptionLabel={(option) => option.name || ""}
              loading={loadingDestinationGeofences}
              onInputChange={(event, newInputValue) => handleSearchTermDestinationGeofenceChange(newInputValue)}
              onChange={(event, newValue) => setFilters({ ...filters, destination_geofence_id: newValue?.id, destinationGeofence: newValue })}
              value={filters.destinationGeofence}
              renderInput={(params) => <TextField {...params} label={t("TRANSACTIONS.destination_geofence")} size="small" variant="standard" />}
            />
            <CustomDateRange
              title={t('TRANSACTIONS.created_at')}
              showTitle={true}
              dateRange={filters}
              setDateRange={(value) => {
                setFilters({
                  ...filters,
                  ...value
                })
              }}
            />
          </Box>
        }
      />
    </Box>
  );
}
